import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '../lib/api.ts';
import { dateTime, num } from '../lib/format.ts';

interface Kpis {
  scansToday: number;
  qtyToday: number;
  sessionsToday: number;
  pendingWriteTasks: number;
}

interface ScanRow {
  id: number;
  scanTime: string;
  sku: string | null;
  qty: number;
  sessionId: string | null;
  workflowType: string | null;
  operator: string | null;
  location: string | null;
  epc: string | null;
}

interface SessionRow {
  sessionId: string;
  workflowType: string | null;
  firstScanAt: string;
  lastScanAt: string;
  totalScans: number;
  totalQty: number;
  distinctSkus: number;
}

const WORKFLOW_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'pick', label: 'Picks (DO)' },
  { key: 'grn', label: 'GRN (PO)' },
  { key: 'stocktake', label: 'Stocktake' },
  { key: 'return', label: 'Returns' },
];

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function defaultRange() {
  const today = new Date();
  const from = new Date(today.getTime() - 6 * 24 * 3600 * 1000);
  return { from: ymd(from), to: ymd(today) };
}

export function OperationsPage() {
  const [range, setRange] = useState(defaultRange);
  const [workflow, setWorkflow] = useState<string>('');
  const [tab, setTab] = useState<'sessions' | 'scans'>('sessions');

  const kpis = useQuery({
    queryKey: ['ops', 'kpis'],
    queryFn: () => apiFetch<Kpis>('/api/dashboard/operations/kpis'),
    refetchInterval: 30_000,
  });

  const qs = `from=${range.from}&to=${range.to}${workflow ? `&workflowType=${workflow}` : ''}`;
  const sessions = useQuery({
    queryKey: ['ops', 'sessions', range.from, range.to, workflow],
    queryFn: () => apiFetch<{ rows: SessionRow[] }>(`/api/dashboard/operations/sessions?${qs}`),
    refetchInterval: 30_000,
  });
  const scans = useQuery({
    queryKey: ['ops', 'scans', range.from, range.to, workflow],
    queryFn: () => apiFetch<{ rows: ScanRow[] }>(`/api/dashboard/operations/scans?${qs}`),
    enabled: tab === 'scans',
    refetchInterval: 30_000,
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Operations</h1>
        <span className="main-subtitle">
          Live scan activity from SmartLink · refreshes every 30s
        </span>
      </header>

      {kpis.data && (
        <div className="kpi-grid">
          <KPI label="Scans today" value={num(kpis.data.scansToday)} />
          <KPI label="Quantity scanned" value={num(kpis.data.qtyToday)} />
          <KPI label="Sessions today" value={num(kpis.data.sessionsToday)} sub="distinct order_no" />
          <KPI label="Pending write tasks" value={num(kpis.data.pendingWriteTasks)} sub="awaiting approval" flag={kpis.data.pendingWriteTasks > 0} />
        </div>
      )}

      <section className="panel" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>From</label>
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
            style={{ padding: '0.3rem' }}
          />
          <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>To</label>
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
            style={{ padding: '0.3rem' }}
          />
          <span style={{ width: 12 }} />
          {WORKFLOW_FILTERS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWorkflow(w.key)}
              style={{
                background: workflow === w.key ? 'var(--accent)' : 'var(--surface-2)',
                color: workflow === w.key ? 'var(--bg)' : 'var(--text)',
                border: '1px solid var(--border)',
                padding: '0.35rem 0.8rem',
                cursor: 'pointer',
                fontSize: 12,
                borderRadius: 2,
                fontWeight: workflow === w.key ? 600 : 400,
              }}
            >
              {w.label}
            </button>
          ))}
        </div>
      </section>

      <div className="toolbar" style={{ gap: 4 }}>
        {(['sessions', 'scans'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              background: tab === t ? 'var(--accent)' : 'var(--surface-2)',
              color: tab === t ? 'var(--bg)' : 'var(--text)',
              border: '1px solid var(--border)',
              padding: '0.4rem 0.9rem',
              cursor: 'pointer',
              fontSize: 12,
              borderRadius: 2,
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t === 'sessions' ? 'By session' : 'All scans'}
          </button>
        ))}
      </div>

      {tab === 'sessions' && (
        <section className="panel">
          <header className="panel-header">
            <h2 className="panel-title">Sessions in range</h2>
            <span className="panel-subtitle">
              {sessions.data ? `${sessions.data.rows.length} sessions` : ''}
            </span>
          </header>
          {sessions.isLoading && <div className="loading">Loading…</div>}
          {sessions.error && <div className="error">{(sessions.error as Error).message}</div>}
          {sessions.data && sessions.data.rows.length === 0 && (
            <div className="empty">No scan sessions in this range.</div>
          )}
          {sessions.data && sessions.data.rows.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order / session</th>
                  <th>Type</th>
                  <th>First scan</th>
                  <th>Last scan</th>
                  <th className="num">Scans</th>
                  <th className="num">SKUs</th>
                  <th className="num">Total qty</th>
                </tr>
              </thead>
              <tbody>
                {sessions.data.rows.map((s) => (
                  <tr key={s.sessionId}>
                    <td className="mono">{s.sessionId}</td>
                    <td>
                      <span className={`badge ${workflowBadge(s.workflowType)}`}>
                        {s.workflowType ?? '—'}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{dateTime(s.firstScanAt)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{dateTime(s.lastScanAt)}</td>
                    <td className="num">{num(s.totalScans)}</td>
                    <td className="num">{num(s.distinctSkus)}</td>
                    <td className="num">{num(s.totalQty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === 'scans' && (
        <section className="panel">
          <header className="panel-header">
            <h2 className="panel-title">Scan events</h2>
            <span className="panel-subtitle">
              {scans.data ? `${scans.data.rows.length} shown · latest first` : ''}
            </span>
          </header>
          {scans.isLoading && <div className="loading">Loading…</div>}
          {scans.error && <div className="error">{(scans.error as Error).message}</div>}
          {scans.data && scans.data.rows.length === 0 && (
            <div className="empty">No scans in this range.</div>
          )}
          {scans.data && scans.data.rows.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>SKU</th>
                  <th className="num">Qty</th>
                  <th>Session / order</th>
                  <th>Type</th>
                  <th>Operator</th>
                </tr>
              </thead>
              <tbody>
                {scans.data.rows.map((s) => (
                  <tr key={s.id}>
                    <td className="mono" style={{ fontSize: 11 }}>{dateTime(s.scanTime)}</td>
                    <td className="mono">{s.sku ?? '—'}</td>
                    <td className="num">{num(s.qty)}</td>
                    <td className="mono">{s.sessionId ?? '—'}</td>
                    <td>
                      <span className={`badge ${workflowBadge(s.workflowType)}`}>
                        {s.workflowType ?? '—'}
                      </span>
                    </td>
                    <td>{s.operator ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}

function KPI({ label, value, sub, flag }: { label: string; value: string; sub?: string; flag?: boolean }) {
  return (
    <div className="kpi-card" style={flag ? { borderColor: 'var(--danger, #ff6b6b)' } : undefined}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

function workflowBadge(w: string | null): string {
  if (w === 'pick') return 'badge-pending';
  if (w === 'grn') return 'badge-ok';
  if (w === 'stocktake') return 'badge-neutral';
  return 'badge-neutral';
}
